/**
 * Фильтры поиска (T11.5, `02_SPEC.md` §8).
 *
 * ## Состояние переживает сворачивание
 *
 * Telegram выгружает приложение из памяти, когда человек переключается
 * на переписку, — а переключается он постоянно: посмотреть объявление,
 * спросить у жены, вернуться. Потеря набранных фильтров означает, что
 * подбор придётся начинать заново, и второй раз человек этого не
 * сделает.
 *
 * Поэтому фильтры пишутся в хранилище при каждом изменении, а не при
 * уходе с экрана: события «уходит» у выгруженного приложения нет.
 *
 * ## Пустое поле не то же, что ноль
 *
 * «Цена до 0» не найдёт ничего, а «цена не задана» найдёт всё. Поэтому
 * незаданное отсутствует в объекте, а не равно нулю: договор
 * (`filters.v1.json`) требует того же, и подписка, созданная из
 * фильтров, уходит на сервер как есть.
 */

/** Набор фильтров. Поля соответствуют `filters.v1.json`. */
export interface Filters {
  brand_ids?: number[];
  model_ids?: number[];
  generation_ids?: number[];
  region_ids?: number[];
  city_ids?: number[];
  price_min_minor?: number;
  price_max_minor?: number;
  year_min?: number;
  year_max?: number;
  mileage_max_km?: number;
  owners_max?: number;
  market_delta_max_pct?: number;
  has_vin?: boolean;
  body?: string[];
  gearbox?: string[];
  drive?: string[];
  engine_type?: string[];
  seller_type?: string[];
  sources?: string[];
}

/** Поля-перечисления: у них выбор из готового списка значений. */
export type ChoiceField = 'body' | 'gearbox' | 'drive' | 'engine_type' | 'seller_type' | 'sources';

const STORAGE_KEY = 'xoreva.filters';

/**
 * Чтение сохранённых фильтров.
 *
 * Испорченное хранилище даёт пустой набор, а не отказ: сделать с
 * отказом человек всё равно ничего не сможет, а пустой поиск он хотя бы
 * увидит.
 */
export function loadFilters(): Filters {
  const raw = localStorage.getItem(STORAGE_KEY);

  if (raw === null) return {};

  try {
    const parsed: unknown = JSON.parse(raw);

    return typeof parsed === 'object' && parsed !== null ? (parsed as Filters) : {};
  } catch {
    return {};
  }
}

export function saveFilters(filters: Filters): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
}

/**
 * Установка одного поля.
 *
 * Пустое значение удаляет поле, а не записывает ноль или пустой список:
 * «не задано» и «задано пустым» означают разное, и второе не найдёт
 * ничего.
 */
export function withField<K extends keyof Filters>(
  filters: Filters,
  field: K,
  value: Filters[K] | undefined,
): Filters {
  const next = { ...filters };

  const empty =
    value === undefined ||
    value === null ||
    (Array.isArray(value) && value.length === 0) ||
    (typeof value === 'number' && !Number.isFinite(value));

  if (empty) delete next[field];
  else next[field] = value;

  return next;
}

/** Переключение значения в списке: выбрал — добавилось, нажал ещё раз — убралось. */
export function toggle(filters: Filters, field: ChoiceField, value: string): Filters {
  const current = filters[field] ?? [];
  const next = current.includes(value)
    ? current.filter((one) => one !== value)
    : [...current, value];

  return withField(filters, field, next);
}

/**
 * Сколько фильтров задано.
 *
 * Показывается рядом со сбросом: без числа человек не поймёт, почему
 * выдача пуста, и обвинит сервис вместо своих же настроек.
 */
export function countFilters(filters: Filters): number {
  return Object.keys(filters).length;
}

/** Рубли в копейки: человек вводит рубли, договор хранит копейки. */
export function toMinor(rubles: string): number | undefined {
  const digits = rubles.replace(/\s/gu, '');

  if (digits === '') return undefined;

  const value = Number(digits);

  return Number.isFinite(value) && value > 0 ? Math.round(value * 100) : undefined;
}

/** Копейки в рубли для поля ввода. */
export function toRubles(minor: number | undefined): string {
  return minor === undefined ? '' : String(Math.round(minor / 100));
}

/** Целое из поля ввода. Ноль и мусор означают «не задано». */
export function toWhole(text: string): number | undefined {
  const value = Number(text.replace(/\s/gu, ''));

  return Number.isFinite(value) && value > 0 ? Math.round(value) : undefined;
}

/**
 * Строка запроса из фильтров.
 *
 * Списки уходят одним параметром через запятую — `?region_ids=3,1`.
 * Так их принимает API, и только так (`listings.controller.ts`):
 * повторение параметра он отвергает целиком, с `VALIDATION_ERROR`.
 *
 * Здесь было написано обратное — «списки уходят повторяющимся
 * параметром», — и написано с самого начала. Расхождение молчало, пока
 * ни у одного фильтра не бывало больше одного значения: список из
 * одного элемента выглядит одинаково при любом способе. Стоило
 * разрешить выбрать несколько регионов, как поиск перестал отвечать —
 * при любых марке и модели.
 *
 * Пустой список не уходит вовсе. `region_ids=` API разбирает как
 * список из нуля значений и отвергает запрос, а пустой фильтр обязан
 * означать «любой», а не отказ.
 */
export function toQuery(filters: Filters): string {
  const query = new URLSearchParams();

  for (const [field, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      if (value.length > 0) query.append(field, value.join(','));
    } else if (value !== undefined) {
      query.append(field, String(value));
    }
  }

  return query.toString();
}
